
import { useState, useEffect } from 'react';
import { database, type Industry } from '../lib/database';

export const useIndustries = () => {
  const [industries, setIndustries] = useState<Industry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const loadIndustries = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await database.getIndustries();
      setIndustries(data);
    } catch (err) {
      console.error('Error loading industries:', err);
      setError(err instanceof Error ? err.message : 'Failed to load industries');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadIndustries();
  }, []);

  const createIndustry = async (industry: Omit<Industry, 'id' | 'createdDate'>) => {
    try {
      const newIndustry = await database.createIndustry(industry);
      setIndustries(prev => [...prev, newIndustry]);
      return newIndustry;
    } catch (err) {
      console.error('Error creating industry:', err);
      throw err;
    }
  };

  const updateIndustry = async (id: number, updates: Partial<Industry>) => {
    try {
      const updatedIndustry = await database.updateIndustry(id, updates);
      setIndustries(prev => prev.map(ind => ind.id === id ? updatedIndustry : ind));
      return updatedIndustry;
    } catch (err) {
      console.error('Error updating industry:', err);
      throw err;
    }
  };

  const deleteIndustry = async (id: number) => {
    try {
      await database.deleteIndustry(id);
      setIndustries(prev => prev.filter(ind => ind.id !== id));
    } catch (err) {
      console.error('Error deleting industry:', err);
      throw err;
    }
  };

  const getIndustryById = (id: number): Industry | undefined => {
    return industries.find(industry => industry.id === id);
  };

  const getIndustryByName = (name: string): Industry | undefined => {
    return industries.find(industry => 
      industry.name.toLowerCase() === name.toLowerCase()
    );
  };

  return {
    industries,
    loading,
    error,
    loadIndustries,
    createIndustry,
    updateIndustry,
    deleteIndustry,
    getIndustryById,
    getIndustryByName
  };
};
